if (!customElements.get("line-item")) {
  class LineItem extends HTMLElement {
    constructor() {
      super();
      this.selectors = {
        cart: this.closest(".cart"),
        quantity: this.querySelector('input[name="quantity"]'),
        remove: this.querySelector("[data-line-item-remove]"),
      };

      this.selectors.remove?.addEventListener("click", (event) => {
        event.preventDefault();
        this.updateQuantity(0);
      });

      this.selectors.quantity?.addEventListener("change", (event) => {
        event.preventDefault();
        this.updateQuantity(event.target.value);
      });
    }

    updateQuantity(quantity) {
      const requestedQuantity = Number(quantity);
      this.selectors.cart?.classList.add("loading");

      const updates = {};
      updates[this.dataset.key] = requestedQuantity;

      const addonKeys = [];
      if (this.dataset.lineItemRole !== "addon") {
        this.getAddonLines().forEach((line) => {
          if (!line.dataset.key) return;
          updates[line.dataset.key] = requestedQuantity;
          addonKeys.push(line.dataset.key);
        });
      }

      // The inventory reconciliation is part of this one cart operation.
      // Analytics reads confirmed snapshots around both requests together.
      const update = () => this.postUpdates(updates)
        .then((cart) => this.reconcileCappedQuantity(cart, requestedQuantity, addonKeys));
      const operation = typeof window.IWCAnalytics?.runCartOperation === "function"
        ? window.IWCAnalytics.runCartOperation(update, null, { keys: Object.keys(updates) })
        : update();

      return operation
        .catch((error) => {
          console.error("[LineItem] Cart update failed", error);
          this.selectors.cart?.classList.remove("loading");
          this.showUpdateError(error?.isCartResponseError ? error.message : "");
        })
        .finally(() => {
          // Always re-render from server truth: after an error the DOM inputs
          // may no longer match the real cart, and cart-section clears the
          // loading state at the end of its refresh chain.
          window.dispatchEvent(new CustomEvent("cartAdded"));
          window.dispatchEvent(new CustomEvent("cartCounterUpdate"));
        });
    }

    postUpdates(updates) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);

      return fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates }),
        signal: controller.signal,
      })
        .then((response) => {
          if (!response.ok) {
            return response
              .json()
              .catch(() => ({}))
              .then((data) => {
                const updateError = new Error(typeof data.description === "string" ? data.description : "");
                // Only a Shopify description is customer-readable; anything
                // else falls back to the localized generic message.
                updateError.isCartResponseError = true;
                throw updateError;
              });
          }
          return response.json();
        })
        .finally(() => clearTimeout(timeout));
    }

    // /cart/update.js silently caps a quantity at the available inventory.
    // Add-on lines mirror the base quantity, so a capped base must pull its
    // add-ons down to the same value or the configuration group desyncs.
    reconcileCappedQuantity(cart, requestedQuantity, addonKeys) {
      if (requestedQuantity === 0) return cart;

      const baseItem = (cart.items || []).find((item) => item.key === this.dataset.key);
      const actualQuantity = baseItem ? baseItem.quantity : 0;
      if (actualQuantity === requestedQuantity) return cart;

      this.showQuantityCapped(actualQuantity);
      if (!addonKeys.length) return cart;

      const updates = {};
      addonKeys.forEach((key) => {
        updates[key] = actualQuantity;
      });

      return this.postUpdates(updates);
    }

    showQuantityCapped(actualQuantity) {
      const template = this.getMessageText()?.dataset.quantityCappedMessage || "";
      if (!template) return;
      this.openMessage(template.replace("__QUANTITY__", String(actualQuantity)));
    }

    showUpdateError(description) {
      const text = this.getMessageText();
      if (!text) return;
      this.openMessage(description || text.dataset.defaultMessage || "");
    }

    getMessageText() {
      return document.querySelector("#message-cart-update-error [data-cart-update-error-message]");
    }

    openMessage(message) {
      const text = this.getMessageText();
      if (!text || !message) return;
      text.textContent = message;
      window.dispatchEvent(new CustomEvent("messageOpen", { detail: { id: "message-cart-update-error" } }));
    }

    getAddonLines() {
      const container = this.selectors.cart || document;
      const lines = Array.from(container.querySelectorAll("line-item"));
      const configKey = this.dataset.configKey || "";
      const iwcConfigurationKey = this.dataset.iwcConfigurationKey || "";

      return lines.filter((line) => {
        if (line === this || line.dataset.lineItemRole !== "addon") return false;
        const addonConfigKey = line.dataset.configKey || "";
        const addonIwcConfigurationKey = line.dataset.iwcConfigurationKey || "";

        // The full choice separates otherwise identical bed/frame groups.
        // Never join a new line to a legacy one by the old key or variant.
        if (iwcConfigurationKey || addonIwcConfigurationKey) {
          return Boolean(iwcConfigurationKey) &&
            addonIwcConfigurationKey === iwcConfigurationKey && addonConfigKey === configKey;
        }
        if (configKey) return addonConfigKey === configKey;
        return !addonConfigKey &&
          (line.dataset.parentVariantId || line.dataset.parentBedVariant) === this.dataset.id;
      });
    }
  }

  customElements.define("line-item", LineItem);
}
